/** "Add to collection": pick one of your collections (✓ = already there) or create one; one reel or a selection. */
import { useEffect, useRef, useState } from 'react';
import { Check, FolderPlus } from 'lucide-react';
import { Button, cn, toast } from '@vw/ui';
import { api, type CollectionSummary } from '../api';
import { navigate } from '../router';

export function AddToCollection({ reelPks, label = 'Add to collection', onDone, align = 'left', compact }: { reelPks: string[]; label?: string; onDone?: () => void; align?: 'left' | 'right'; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<CollectionSummary[] | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    api<{ collections: CollectionSummary[] }>('/api/collections').then((r) => setList(r.collections.filter((c) => !c.takedown)), () => setList([]));
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    // Capture phase + stopPropagation: Esc closes only this popover, not a dialog it sits in (the reel inspector).
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key, true); };
  }, [open]);

  const done = (id: string, text: string) => {
    toast.success(text, { action: { label: 'Open', onClick: () => navigate(`/collections/${id}`) } });
    setOpen(false);
    onDone?.();
  };
  async function add(c: CollectionSummary) {
    setBusy(true);
    try {
      const r = await api<{ added: number }>(`/api/collections/${c.id}/items`, { method: 'POST', json: { reelPks } });
      done(c.id, r.added ? `Added ${r.added} to ${c.name}` : `Already in ${c.name}`);
    } catch (e) {
      toast.error('Could not add to the collection', { description: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }
  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const c = await api<{ id: string; name: string }>('/api/collections', { method: 'POST', json: { name, reelPks } });
      setName('');
      done(c.id, `Created ${c.name}`);
    } catch (e) {
      toast.error('Could not create the collection', { description: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative inline-block" ref={ref}>
      <Button variant="secondary" size={compact ? 'sm' : 'md'} onClick={() => setOpen(!open)} disabled={!reelPks.length} aria-expanded={open}><FolderPlus className="h-3.5 w-3.5" />{label}</Button>
      {open && (
        <div role="dialog" aria-label="Add to collection" className={cn('absolute top-[calc(100%+6px)] z-40 w-72 animate-zoom-in space-y-2 rounded-2xl border border-border bg-card p-2.5 shadow-2xl', align === 'right' ? 'right-0' : 'left-0')}>
          {!list ? <p className="p-2 text-xs text-muted-foreground">Loading…</p> : (
            <ul className="max-h-56 overflow-y-auto">
              {list.map((c) => (
                <li key={c.id}><button disabled={busy} onClick={() => add(c)} className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-secondary">
                  <span className="truncate">{c.emoji ?? '📁'} {c.name}</span><span className="text-xs text-muted-foreground">{c.itemCount}</span>
                </button></li>
              ))}
              {!list.length && <li className="p-2 text-xs text-muted-foreground">No collections yet. Create one below.</li>}
            </ul>
          )}
          <form className="flex gap-1.5 border-t border-border pt-2" onSubmit={(e) => { e.preventDefault(); void create(); }}>
            <input aria-label="New collection name" placeholder="New collection" value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
              className="min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
            <Button type="submit" size="sm" disabled={!name.trim()} loading={busy}><Check className="h-3.5 w-3.5" />Create</Button>
          </form>
        </div>
      )}
    </div>
  );
}
